import React, {useState, useEffect, useRef} from 'react';
import styles from './style.module.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPalette} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'next-i18next';

const AccessibilityButton: React.FC = () => {
  const {t} = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [isHighContrast, setIsHighContrast] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleModal();
    }
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      modalRef.current?.focus();
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      buttonRef.current?.focus();
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isModalOpen]);

  const adjustFontSize = (size: number) => {
    setFontSize(size);
    document.documentElement.style.fontSize = `${size}px`;

    // Remove all scale classes
    document.body.classList.remove(
      'font-scale-small',
      'font-scale-medium',
      'font-scale-large'
    );

    // Add the appropriate scale class
    if (size === 14) {
      document.body.classList.add('font-scale-small');
    } else if (size === 16) {
      document.body.classList.add('font-scale-medium');
    } else if (size === 30) {
      document.body.classList.add('font-scale-large');
    }
  };

  const toggleHighContrast = () => {
    setIsHighContrast(prev => !prev);
    if (!isHighContrast) {
      document.body.setAttribute('data-theme', 'high-contrast');
    } else {
      document.body.removeAttribute('data-theme');
    }
  };

  return (
    <div className={styles.accessibilityContainer}>
      <button
        ref={buttonRef}
        className={styles.accessibilityButton}
        onClick={toggleModal}
        onKeyDown={handleKeyDown}
        aria-label={t('accessibility_button_label')}
        aria-expanded={isModalOpen}
        type="button"
      >
        <FontAwesomeIcon className={styles.icon} icon={faPalette} />
      </button>

      {isModalOpen && (
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="accessibility-modal-title"
          tabIndex={-1}
          ref={modalRef}
        >
          <div className={`${styles.modalContent} modal-content`}>
            <h2 id="accessibility-modal-title" className={styles.modalTitle}>
              {t<string>('accessibilityButton.title')}
            </h2>
            <button
              className={styles.closeButton}
              onClick={toggleModal}
              aria-label="Close accessibility settings"
            >
              ✕
            </button>

            <div className={styles.settings}>
              <div className={styles.settingGroup}>
                <label htmlFor="fontSizeSelector">
                  {t<string>('accessibilityButton.font_size')}
                </label>
                <div id="fontSizeSelector" className={styles.buttonGroup}>
                  <button
                    onClick={() => adjustFontSize(14)}
                    aria-pressed={fontSize === 14}
                    className={fontSize === 14 ? styles.active : ''}
                  >
                    {t<string>('accessibilityButton.font_size_small')}
                  </button>
                  <button
                    onClick={() => adjustFontSize(16)}
                    aria-pressed={fontSize === 16}
                    className={fontSize === 16 ? styles.active : ''}
                  >
                    {t<string>('accessibilityButton.font_size_medium')}
                  </button>
                  <button
                    onClick={() => adjustFontSize(30)}
                    aria-pressed={fontSize === 30}
                    className={fontSize === 30 ? styles.active : ''}
                  >
                    {t<string>('accessibilityButton.font_size_large')}
                  </button>
                </div>
              </div>

              <div className={styles.settingGroup}>
                <label htmlFor="highContrastToggle">
                  {t<string>('accessibilityButton.high_contrast_mode')}
                </label>
                <button
                  id="highContrastToggle"
                  onClick={toggleHighContrast}
                  aria-pressed={isHighContrast}
                  className={isHighContrast ? styles.active : ''}
                >
                  {t<string>(
                    isHighContrast
                      ? 'accessibilityButton.disable'
                      : 'accessibilityButton.enable'
                  )}{' '}
                  {t<string>('accessibilityButton.high_contrast')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityButton;
